import { useState, useRef, useEffect, useMemo, ReactNode, ChangeEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { LoadingSpinner, PageLoader } from '../components/LoadingSpinner';
import FrontendPreview, { EMBEDDING_BATCH_LIMITS, MINIAPP_MAX_PAYLOAD_BYTES } from '../components/FrontendPreview';
import { useProject, useConversations, useMessages, useProjectCode, useFileVersions, FormDataEntry } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useUsage } from '../hooks/useUsage';
import { useModelCreditCosts } from '../hooks/useModelCreditCosts';
import { useReadyVideos, getVideoSrc } from '../hooks/useFunVideos';
import { VISIBLE_AGENTIC_CODER_MODELS, type VisibleAgenticCoderModelId } from '../lib/agentModels';
import { PAYMENTS_DISABLED, PAYMENTS_DISABLED_MESSAGE } from '../lib/payments';
import { ArrowLeft, Send, User, AlertCircle, Code, RefreshCw, RefreshCcw, RotateCcw, Check, Copy, ChevronDown, Eye, Film, Paperclip, X } from 'lucide-react';

// Helper function to render message content with placeholder links and attachment pills
function renderMessageContent(content: string, isUser?: boolean): ReactNode {
  // Define placeholders and their corresponding links
  const placeholders: { pattern: string; link: ReactNode }[] = [
    {
      pattern: '{{UPGRADE_LINK}}',
      link: PAYMENTS_DISABLED
        ? <span key="upgrade" className="text-theme-text-muted">{PAYMENTS_DISABLED_MESSAGE}</span>
        : <Link key="upgrade" to="/pricing" className="text-accent-500 hover:text-accent-600 underline font-medium">View subscription plans</Link>
    },
    {
      pattern: '{{GUIDE_LINK}}',
      link: <Link key="guide" to="/guide" className="text-accent-500 hover:text-accent-600 underline font-medium">User Guide</Link>
    }
  ];

  // Extract attachment labels from the end of the message
  const attachmentRegex = /\n?\n?(\[Attached: [^\]]+\]\n?)+$/;
  const attachmentMatch = content.match(attachmentRegex);
  let mainContent = content;
  let attachmentNames: string[] = [];
  if (attachmentMatch) {
    mainContent = content.slice(0, attachmentMatch.index).trimEnd();
    const nameRegex = /\[Attached: ([^\]]+)\]/g;
    let m;
    while ((m = nameRegex.exec(attachmentMatch[0])) !== null) {
      attachmentNames.push(m[1]);
    }
  }

  // Process placeholders in main content
  const hasPlaceholder = placeholders.some(p => mainContent.includes(p.pattern));
  let textNode: ReactNode = mainContent;
  if (hasPlaceholder) {
    const parts: ReactNode[] = [];
    let remaining = mainContent;
    let keyIndex = 0;

    while (remaining.length > 0) {
      let earliestIndex = remaining.length;
      let matchedPlaceholder: typeof placeholders[0] | null = null;

      for (const placeholder of placeholders) {
        const index = remaining.indexOf(placeholder.pattern);
        if (index !== -1 && index < earliestIndex) {
          earliestIndex = index;
          matchedPlaceholder = placeholder;
        }
      }

      if (matchedPlaceholder && earliestIndex < remaining.length) {
        if (earliestIndex > 0) {
          parts.push(<span key={keyIndex++}>{remaining.slice(0, earliestIndex)}</span>);
        }
        parts.push(<span key={keyIndex++}>{matchedPlaceholder.link}</span>);
        remaining = remaining.slice(earliestIndex + matchedPlaceholder.pattern.length);
      } else {
        parts.push(<span key={keyIndex++}>{remaining}</span>);
        break;
      }
    }
    textNode = <>{parts}</>;
  }

  if (attachmentNames.length === 0) return textNode;

  return (
    <>
      {textNode}
      <span className="flex flex-wrap gap-1 mt-2">
        {attachmentNames.map((name, i) => (
          <span
            key={i}
            className={
              'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] leading-tight ' +
              (isUser
                ? 'bg-white/20 text-white/90'
                : 'bg-theme-surface-hover text-theme-text-secondary')
            }
          >
            <Paperclip className="h-2.5 w-2.5 flex-shrink-0" />
            {name}
          </span>
        ))}
      </span>
    </>
  );
}

// Available models (Claude, Kimi, and GLM)
const MODELS = VISIBLE_AGENTIC_CODER_MODELS;

type ModelId = VisibleAgenticCoderModelId;

const MODEL_STORAGE_KEY = 'llambada-selected-model';
const MAX_CHAT_ATTACHMENTS = 8;

function getStoredModel(): ModelId {
  const stored = localStorage.getItem(MODEL_STORAGE_KEY);
  if (stored && MODELS.some(m => m.id === stored)) {
    return stored as ModelId;
  }
  return 'kimi-for-coding'; // Default to Kimi for new users
}

function setStoredModel(modelId: ModelId) {
  localStorage.setItem(MODEL_STORAGE_KEY, modelId);
}

export function ConversationPage() {
  const { projectId, conversationId } = useParams<{
    projectId: string;
    conversationId: string;
  }>();
  const { subscriptionTier, session, user, profile, authReady } = useAuth();
  const { data: usageData, loading: usageLoading } = useUsage();
  const modelCreditCosts = useModelCreditCosts();
  const { locale } = useI18n();
  const { project, loading: projectLoading, streaming, runMiniApp, resetStreaming, refetch: refetchProject } = useProject(projectId);
  const { conversations, updateConversation } = useConversations(projectId);
  const { 
    messages, 
    loading: messagesLoading, 
    sending, 
    error, 
    streamingContent,
    streamingStatus,
    sendMessage,
    resubmitMessage,
    lastModifiedFiles,
    clearLastModifiedFiles,
    buildStarted,
    clearBuildStarted,
  } = useMessages(conversationId, projectId);
  const { files: codeFiles, fetchCode } = useProjectCode(projectId);
  const { rollbackToConversation, checkConversationHasVersions } = useFileVersions(projectId);

  // Get frontend HTML for preview
  const frontendHtml = codeFiles['frontend/index.html'] || '';
  const hasFrontend = !!frontendHtml;

  // Get all frontend files except index.html (for OPFS pre-loading)
  const frontendFiles = useMemo(() => {
    const files: Record<string, string> = {};
    for (const [path, content] of Object.entries(codeFiles)) {
      if (path.startsWith('frontend/') && path !== 'frontend/index.html' && path !== 'frontend/common.css') {
        const filename = path.replace('frontend/', '');
        files[filename] = content;
      }
    }
    return files;
  }, [codeFiles]);

  const [input, setInput] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [resubmittingMessageId, setResubmittingMessageId] = useState<string | null>(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [rollbackSuccess, setRollbackSuccess] = useState(false);
  const [rollbackError, setRollbackError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<ModelId>(getStoredModel);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [chatAttachments, setChatAttachments] = useState<File[]>([]);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [pendingErrorMessage, setPendingErrorMessage] = useState<string | null>(null);
  const [pendingErrorType, setPendingErrorType] = useState<'execution' | 'build'>('execution');
  const [errorHandoffInProgress, setErrorHandoffInProgress] = useState(false);
  
  // Fun videos: show AI-generated videos instead of agent messages
  const { videos: funVideos, getVideoForMessage } = useReadyVideos();
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  
  // During streaming, show a fun video by default instead of the raw agent output
  const [showStreamingText, setShowStreamingText] = useState(false);
  const [streamingVideo, setStreamingVideo] = useState<ReturnType<typeof getVideoForMessage>>(null);
  const streamingVideoPickedRef = useRef(false);
  
  // Local build status override for immediate UI feedback when dependencies change
  // This mirrors the pattern used in ProjectPage for consistency
  const [localBuildStatus, setLocalBuildStatus] = useState<string | null>(null);
  
  // Effective build status: local override takes precedence, then DB value
  const effectiveBuildStatus = localBuildStatus || project?.build_status;
  
  const lastHandledErrorRef = useRef<string | null>(null); // Track last handled error to prevent re-triggering
  const lastHandledBuildErrorRef = useRef<string | null>(null); // Track last handled build error
  const prevDbBuildStatus = useRef(project?.build_status); // Track DB status changes
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatAttachmentInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const conversation = conversations.find((c) => c.id === conversationId);

  // Auto-resize textarea based on content (max 10 rows)
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Calculate line height (approximately 24px per line)
      const lineHeight = 24;
      const maxRows = 10;
      const maxHeight = lineHeight * maxRows;
      // Set new height, capped at maxHeight
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
      // Add overflow when content exceeds max height
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }
  }, [input]);

  // Pick a stable fun video when streaming starts, reset when it ends
  useEffect(() => {
    if (sending && funVideos.length > 0 && !streamingVideoPickedRef.current) {
      // Pick a random video for this streaming session
      const randomIndex = Math.floor(Math.random() * funVideos.length);
      setStreamingVideo(funVideos[randomIndex]);
      streamingVideoPickedRef.current = true;
      setShowStreamingText(false); // Reset to video view for each new message
    }
    if (!sending) {
      streamingVideoPickedRef.current = false;
      setStreamingVideo(null);
    }
  }, [sending, funVideos]);

  // Check if this conversation has any file versions associated with it
  const [hasVersions, setHasVersions] = useState(false);
  
  useEffect(() => {
    if (conversationId) {
      checkConversationHasVersions(conversationId).then(setHasVersions);
    }
  }, [conversationId, checkConversationHasVersions]);

  // Check for pending error from ProjectPage navigation
  // This auto-sends the error message when coming from the "Fix with AI" flow
  useEffect(() => {
    if (conversationId && session && authReady && messages.length === 0 && !sending && !messagesLoading) {
      const storageKey = `pendingError_${conversationId}`;
      const pendingError = sessionStorage.getItem(storageKey);
      if (pendingError) {
        sessionStorage.removeItem(storageKey);
        setErrorHandoffInProgress(true);
        // Auto-send the error message after a short delay to ensure everything is loaded
        setTimeout(async () => {
          try {
            await sendMessage(pendingError, selectedModel);
          } finally {
            setErrorHandoffInProgress(false);
          }
        }, 500);
      }
    }
  }, [conversationId, session, authReady, messages.length, sending, messagesLoading, sendMessage, selectedModel]);

  // Check for pending message from HomePage AI build flow
  // This auto-sends the initial build request when coming from the "Let AI build" flow
  useEffect(() => {
    if (conversationId && session && authReady && messages.length === 0 && !sending && !messagesLoading) {
      const storageKey = `pendingMessage_${conversationId}`;
      const pendingMessage = sessionStorage.getItem(storageKey);
      if (pendingMessage) {
        sessionStorage.removeItem(storageKey);
        // Auto-send the message after a short delay to ensure everything is loaded
        setTimeout(() => {
          sendMessage(pendingMessage, selectedModel);
        }, 500);
      }
    }
  }, [conversationId, session, authReady, messages.length, sending, messagesLoading, sendMessage, selectedModel]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  // Track previous sending state to detect when Claude Code finishes
  const prevSendingRef = useRef(sending);
  
  useEffect(() => {
    const wasSending = prevSendingRef.current;
    prevSendingRef.current = sending;
    
    if (!sending) {
      inputRef.current?.focus();
      // Refresh code files after Claude Code finishes
      if (messages.length > 0) {
        fetchCode();
      }
      
      // Clear preview errors when Claude Code finishes updating code
      // This ensures the preview doesn't show stale errors after a fix
      if (wasSending) {
        setPreviewError(null);
        resetStreaming();
        // Refetch project to pick up build_status changes (e.g. when REBUILD_BACKEND
        // triggered a build). The DB is already updated by the time the SSE stream ends,
        // so this reliably reflects the current build state even if the SSE build_started
        // event was missed due to buffering/timing.
        refetchProject();
      }
    }
  }, [sending, messages.length, fetchCode, resetStreaming, refetchProject]);

  // Check if dependencies were modified and set building status immediately
  // This provides instant UI feedback before the DB updates
  useEffect(() => {
    if (lastModifiedFiles.length > 0) {
      const dependencyFiles = ['Dockerfile', 'requirements.txt', 'cloud-run.json'];
      const dependenciesModified = lastModifiedFiles.some(file =>
        dependencyFiles.some(dep => file === dep || file.endsWith('/' + dep))
      );
      
      if (dependenciesModified) {
        // Set local build status immediately for instant UI feedback
        setLocalBuildStatus('building');
        // Clear the modified files list after processing
        clearLastModifiedFiles();
      }
    }
  }, [lastModifiedFiles, clearLastModifiedFiles]);

  // Handle build_started event from SSE - this fires IMMEDIATELY when backend triggers a build,
  // providing instant UI feedback to block the preview. This is faster than waiting for the
  // 'complete' event which only arrives at the end of the agent's response.
  useEffect(() => {
    if (buildStarted) {
      // Set local build status immediately for instant UI feedback
      setLocalBuildStatus('building');
      // Clear the flag after processing
      clearBuildStarted();
    }
  }, [buildStarted, clearBuildStarted]);

  // Clear local override when DB status changes to match or indicates completion
  useEffect(() => {
    const dbStatus = project?.build_status;
    const prevStatus = prevDbBuildStatus.current;
    prevDbBuildStatus.current = dbStatus;
    
    // Only clear local override when DB status actually changes
    if (prevStatus !== dbStatus) {
      // Only clear localBuildStatus when the build is done in the DB.
      // Do NOT clear it when the DB transitions to 'building' — that causes a race where
      // localBuildStatus is nulled before project?.build_status is confirmed 'building',
      // making effectiveBuildStatus briefly drop to 'ready' and hiding the modal.
      if ((dbStatus === 'ready' || dbStatus === 'failed') && prevStatus === 'building') {
        // Build completed in DB, clear local override
        setLocalBuildStatus(null);
      }
    }
  }, [project?.build_status, localBuildStatus]);

  // Poll for build status updates while building - calls check-build-status 
  // which checks Cloud Build and updates Cloud Run/DB if build completed
  useEffect(() => {
    if (effectiveBuildStatus !== 'building' || !projectId) return;
    
    const checkBuildStatus = async () => {
      if (!session) return;
      
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        
        const response = await fetch(
          `${supabaseUrl}/functions/v1/check-build-status?projectId=${projectId}`,
          {
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
            },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          // If build completed (success or failure), refetch project to get updated data
          if (data.buildStatus === 'ready' || data.buildStatus === 'failed') {
            setLocalBuildStatus(null); // Clear local override
            refetchProject();
          }
        }
      } catch (err) {
        console.warn('Error checking build status:', err);
      }
    };
    
    // Delay first check to give DB time to update after build trigger
    const initialDelay = setTimeout(checkBuildStatus, 3000);
    
    // Then poll every 10 seconds
    const pollInterval = setInterval(checkBuildStatus, 10000);
    
    return () => {
      clearTimeout(initialDelay);
      clearInterval(pollInterval);
    };
  }, [effectiveBuildStatus, projectId, refetchProject]);

  // Detect build failures and show the error modal with build logs
  useEffect(() => {
    if (effectiveBuildStatus !== 'failed' || !projectId || !session || sending || errorHandoffInProgress) return;
    // Don't re-trigger if we already handled this build failure
    const buildErrorKey = `build-failed-${project?.last_build_id || projectId}`;
    if (lastHandledBuildErrorRef.current === buildErrorKey) return;
    lastHandledBuildErrorRef.current = buildErrorKey;

    let cancelled = false;

    // Fetch build logs and show the error modal
    (async () => {
      let errorDetails = 'The backend build failed.';
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(
          `${supabaseUrl}/functions/v1/get-build-logs?projectId=${projectId}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.logs) {
            errorDetails = `The backend Docker build failed. Build logs:\n\n${data.logs}`;
          }
        }
      } catch (_) {
        // Use generic message if logs can't be fetched
      }
      const errorMessage = `${errorDetails}\n\nPlease help me fix this build error.`;
      if (cancelled) return;
      setPendingErrorMessage(errorMessage);
      setPendingErrorType('build');
      setShowErrorModal(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveBuildStatus, projectId, session, project?.last_build_id, sending, errorHandoffInProgress]);

  // Reset build error ref when a new build starts
  useEffect(() => {
    if (effectiveBuildStatus === 'building') {
      lastHandledBuildErrorRef.current = null;
    }
  }, [effectiveBuildStatus]);

  // Reset handled error ref when a new run starts
  useEffect(() => {
    if (streaming.status === 'authenticating' || streaming.status === 'running') {
      lastHandledErrorRef.current = null;
    }
  }, [streaming.status]);

  // Detect run log errors and show the error modal
  useEffect(() => {
    if (sending || errorHandoffInProgress) return;

    // Only trigger when run completes (not during running/authenticating)
    if (streaming.status !== 'completed' && streaming.status !== 'failed') return;
    
    const logs = streaming.logs;
    if (!logs) return;
    
    // Check if there's an error in the run logs
    const hasError = logs.exitCode !== 0 || logs.hasAppError;
    if (!hasError) return;
    
    // Build error message from logs
    let errorDetails = '';
    if (logs.stderr) {
      errorDetails += `stderr:\n${logs.stderr}\n\n`;
    }
    if (logs.stdout) {
      errorDetails += `stdout:\n${logs.stdout}\n\n`;
    }
    if (logs.exitCode !== 0) {
      errorDetails += `Exit code: ${logs.exitCode}`;
    }
    
    const errorMessage = `The mini-app encountered an execution error:\n\n${errorDetails.trim()}\n\nPlease help me fix this issue.`;
    
    // Don't show modal if we've already handled this exact error
    if (lastHandledErrorRef.current === errorMessage) return;

    setPendingErrorMessage(errorMessage);
    setPendingErrorType('execution');
    setShowErrorModal(true);
  }, [streaming.status, streaming.logs, sending, errorHandoffInProgress]);

  useEffect(() => {
    if (sending || errorHandoffInProgress) {
      setShowErrorModal(false);
    }
  }, [sending, errorHandoffInProgress]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setModelDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleModelChange = (modelId: ModelId) => {
    setSelectedModel(modelId);
    setStoredModel(modelId);
    setModelDropdownOpen(false);
  };

  const handleOpenChatAttachmentPicker = () => {
    if (sending) return;
    chatAttachmentInputRef.current?.click();
  };

  const handleChatAttachmentChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (selectedFiles.length === 0) return;

    setChatAttachments((prev) => {
      const next = [...prev];
      for (const file of selectedFiles) {
        if (next.length >= MAX_CHAT_ATTACHMENTS) break;
        const duplicate = next.some(
          (existing) =>
            existing.name === file.name &&
            existing.size === file.size &&
            existing.lastModified === file.lastModified
        );
        if (!duplicate) next.push(file);
      }
      return next;
    });
  };

  const handleRemoveChatAttachment = (index: number) => {
    setChatAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const clearChatAttachments = () => {
    setChatAttachments([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const messageContent = input.trim();
    const attachmentsToSend = [...chatAttachments];
    setInput('');
    setChatAttachments([]);
    
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = '48px';
      inputRef.current.style.overflowY = 'hidden';
    }

    const result = await sendMessage(messageContent, selectedModel, attachmentsToSend);
    if (result.error && attachmentsToSend.length > 0) {
      setChatAttachments(attachmentsToSend);
    }

    // Update conversation title if it's the first message
    if (messages.length === 0 && conversation && result.data) {
      const title = messageContent.slice(0, 50) + (messageContent.length > 50 ? '...' : '');
      await updateConversation(conversationId!, title);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleRollback = async () => {
    if (!conversationId) return;
    
    setRollingBack(true);
    setRollbackError(null);
    
    const result = await rollbackToConversation(conversationId);
    
    setRollingBack(false);
    
    if (result.success) {
      setRollbackSuccess(true);
      await fetchCode();
      setTimeout(() => setRollbackSuccess(false), 3000);
    } else {
      setRollbackError(result.error || 'Failed to rollback');
    }
  };

  const handleCopyMessage = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleResubmitMessage = async (messageId: string) => {
    if (sending || resubmittingMessageId) return;
    
    setResubmittingMessageId(messageId);
    try {
      await resubmitMessage(messageId, selectedModel);
    } finally {
      setResubmittingMessageId(null);
    }
  };

  // Handle frontend preview submit
  const handleFrontendSubmit = async (entries: FormDataEntry[]) => {
    setPreviewError(null);
    const result = await runMiniApp(entries);
    if (result.error) {
      setPreviewError(result.error);
      const isBackendBuilding =
        result.errorCode === 'BACKEND_BUILDING' ||
        /Backend is (still being built|currently rebuilding)/i.test(result.error);
      if (!isBackendBuilding && !sending && !errorHandoffInProgress) {
        // Show error submission modal
        const errorMessage = `The mini-app encountered an execution error:\n\n${result.error}\n\nPlease help me fix this issue.`;
        setPendingErrorMessage(errorMessage);
        setPendingErrorType('execution');
        setShowErrorModal(true);
      }
    }
  };

  // Handle submitting error to Claude Code - auto-submit the error message
  const handleSubmitError = async () => {
    if (pendingErrorMessage && !sending) {
      const errorMessage = pendingErrorMessage;
      lastHandledErrorRef.current = errorMessage; // Mark as handled
      setErrorHandoffInProgress(true);
      setShowErrorModal(false);
      setPendingErrorMessage(null);
      
      // Auto-submit the error message to Claude Code
      try {
        await sendMessage(errorMessage, selectedModel);
      } finally {
        setErrorHandoffInProgress(false);
      }
    }
  };

  // Handle dismissing the error modal - copy error to input field but don't submit
  const handleDismissError = () => {
    if (pendingErrorMessage) {
      lastHandledErrorRef.current = pendingErrorMessage; // Mark as handled
      setInput(pendingErrorMessage);
      
      // Trigger textarea resize after a short delay
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.style.height = 'auto';
          const lineHeight = 24;
          const maxRows = 10;
          const maxHeight = lineHeight * maxRows;
          const newHeight = Math.min(inputRef.current.scrollHeight, maxHeight);
          inputRef.current.style.height = `${newHeight}px`;
          inputRef.current.style.overflowY = inputRef.current.scrollHeight > maxHeight ? 'auto' : 'hidden';
          inputRef.current.focus();
        }
      }, 0);
    }
    setShowErrorModal(false);
    setPendingErrorMessage(null);
  };

  // Handle frontend JS errors from the iframe
  const handleFrontendError = (errorText: string) => {
    if (sending || errorHandoffInProgress) return;
    const errorMessage = `The mini-app encountered a frontend JavaScript error:\n\n${errorText}\n\nPlease help me fix this issue.`;
    setPendingErrorMessage(errorMessage);
    setPendingErrorType('execution');
    setShowErrorModal(true);
  };

  // Find the index of the last assistant message to show preview after it
  const lastAssistantMessageIndex = messages.reduce((lastIdx, msg, idx) => 
    msg.role === 'assistant' ? idx : lastIdx, -1
  );

  if (projectLoading || messagesLoading) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <h1 className="text-2xl font-bold text-theme-text mb-4">Mini-App not found</h1>
          <Link to="/dashboard" className="btn btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </Layout>
    );
  }

  const selectedModelInfo = MODELS.find(m => m.id === selectedModel) || MODELS[0];

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-4rem)]">
        {/* Header */}
        <div className="border-b border-theme-border-muted px-3 sm:px-4 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
            <div className="min-w-0 flex-1">
              <Link
                to={`/project/${projectId}`}
                className="inline-flex items-center gap-2 text-sm text-theme-text-muted hover:text-theme-text transition-colors"
              >
                <ArrowLeft className="h-4 w-4 shrink-0" />
                <span className="truncate">Back to {project.name}</span>
              </Link>
              <h1 className="font-display text-base sm:text-lg font-semibold text-theme-text mt-1 truncate">
                {conversation?.title || 'New Conversation'}
              </h1>
            </div>
            
            {/* Rollback button - show if conversation has associated versions */}
            {hasVersions && messages.length > 0 && !sending && (
              <div className="flex items-center gap-2 shrink-0">
                {rollbackSuccess && (
                  <span className="flex items-center gap-1 text-xs sm:text-sm text-theme-success">
                    <Check className="h-4 w-4" />
                    <span className="hidden sm:inline">Code restored</span>
                  </span>
                )}
                {rollbackError && (
                  <span className="flex items-center gap-1 text-xs sm:text-sm text-theme-error">
                    <AlertCircle className="h-4 w-4" />
                    <span className="hidden sm:inline">{rollbackError}</span>
                  </span>
                )}
                <button
                  onClick={handleRollback}
                  disabled={rollingBack}
                  className="btn btn-secondary text-xs sm:text-sm py-1.5 px-2 sm:px-3 flex items-center gap-1 sm:gap-2"
                  title="Restore code to the state after this conversation"
                >
                  {rollingBack ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">Restore code</span>
                  <span className="sm:hidden">Restore</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4 sm:space-y-6">
          {messages.length === 0 && !sending ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="inline-flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-accent-500/20 to-cyan-500/20 mb-4 sm:mb-6">
                <Code className="h-7 w-7 sm:h-8 sm:w-8 text-accent-400" />
              </div>
              <h2 className="font-display text-lg sm:text-xl font-semibold text-theme-text mb-2">
                Describe your changes
              </h2>
              <p className="text-sm sm:text-base text-theme-text-muted max-w-md">
                Tell the coding AI what modifications you want to make to your mini-app.
                It will edit the frontend and backend code for you.
              </p>
            </div>
          ) : (
            messages.map((message, index) => (
              <div key={message.id}>
                <div
                  className={`flex gap-2 sm:gap-4 ${
                    message.role === 'user' ? 'flex-row-reverse' : ''
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 items-center justify-center rounded-xl ${
                      message.role === 'user'
                        ? 'bg-accent-500'
                        : 'bg-theme-surface'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <User className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    ) : (
                      <Code className="h-4 w-4 sm:h-5 sm:w-5 text-theme-text-secondary" />
                    )}
                  </div>
                  <div
                    className={`flex-1 max-w-[85%] sm:max-w-3xl ${
                      message.role === 'user' ? 'text-right' : ''
                    }`}
                  >
                    {/* Fun video replacement for assistant messages */}
                    {message.role === 'assistant' && funVideos.length > 0 && !expandedMessages.has(message.id) ? (
                      <>
                        <div className="inline-block rounded-2xl px-3 sm:px-4 py-2 sm:py-3 bg-theme-surface text-theme-text rounded-tl-md">
                          {(() => {
                            const video = getVideoForMessage(message.id);
                            if (video) {
                              return (
                                <div className="space-y-2">
                                  <p className="text-xs sm:text-sm text-theme-text-secondary italic">
                                    Let me show you this <span className="font-medium text-accent-500">{video.name}</span> while I&apos;m working on your new app.
                                  </p>
                                  <video
                                    src={getVideoSrc(video)!}
                                    controls
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    className="rounded-lg max-w-full"
                                    style={{ maxHeight: '300px' }}
                                  />
                                </div>
                              );
                            }
                            return (
                              <pre className="whitespace-pre-wrap text-left font-sans text-xs sm:text-sm">
                                {renderMessageContent(message.content, false)}
                              </pre>
                            );
                          })()}
                        </div>
                        <button
                          onClick={() => setExpandedMessages(prev => new Set(prev).add(message.id))}
                          className="flex items-center gap-1 mt-1 text-[10px] sm:text-xs text-theme-text-muted hover:text-accent-500 transition-colors"
                          title="Show actual agent output"
                        >
                          <Eye className="h-3 w-3" />
                          <span>Show actual response</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <div
                          className={`inline-block rounded-2xl px-3 sm:px-4 py-2 sm:py-3 ${
                            message.role === 'user'
                              ? 'bg-accent-500 text-white rounded-tr-md'
                              : 'bg-theme-surface text-theme-text rounded-tl-md'
                          }`}
                        >
                          <pre className="whitespace-pre-wrap text-left font-sans text-xs sm:text-sm">
                            {renderMessageContent(message.content, message.role === 'user')}
                          </pre>
                        </div>
                        {message.role === 'assistant' && funVideos.length > 0 && expandedMessages.has(message.id) && (
                          <button
                            onClick={() => setExpandedMessages(prev => {
                              const next = new Set(prev);
                              next.delete(message.id);
                              return next;
                            })}
                            className="flex items-center gap-1 mt-1 text-[10px] sm:text-xs text-theme-text-muted hover:text-accent-500 transition-colors"
                            title="Show fun video instead"
                          >
                            <Film className="h-3 w-3" />
                            <span>Show fun video</span>
                          </button>
                        )}
                      </>
                    )}
                    <div className={`flex items-center gap-2 mt-1 sm:mt-1.5 ${message.role === 'user' ? 'justify-end' : ''}`}>
                      {message.role === 'user' && (
                        <button
                          onClick={() => handleResubmitMessage(message.id)}
                          disabled={sending || resubmittingMessageId !== null}
                          className="p-1 rounded hover:bg-theme-surface-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Resubmit message"
                        >
                          {resubmittingMessageId === message.id ? (
                            <RefreshCw className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-theme-text-muted animate-spin" />
                          ) : (
                            <RefreshCcw className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-theme-text-muted hover:text-theme-text-secondary" />
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => handleCopyMessage(message.id, message.content)}
                        className="p-1 rounded hover:bg-theme-surface-hover transition-colors"
                        title="Copy message"
                      >
                        {copiedMessageId === message.id ? (
                          <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-theme-success" />
                        ) : (
                          <Copy className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-theme-text-muted hover:text-theme-text-secondary" />
                        )}
                      </button>
                      <p className="text-[10px] sm:text-xs text-theme-text-muted">
                        {new Date(message.created_at).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Show preview panel after the last assistant message */}
                {index === lastAssistantMessageIndex && hasFrontend && !sending && (
                  <div className="mt-4 sm:mt-6 ml-10 sm:ml-14">
                    <div className="text-xs sm:text-sm font-medium text-theme-text-muted mb-2">
                      Preview your app
                    </div>
                    <FrontendPreview
                      html={frontendHtml}
                      frontendFiles={frontendFiles}
                      onSubmit={handleFrontendSubmit}
                      isProcessing={streaming.status === 'authenticating' || streaming.status === 'running'}
                      isComplete={streaming.status === 'completed'}
                      backendError={streaming.status === 'failed' ? (streaming.error || 'Unknown error') : previewError}
                      buildStatus={effectiveBuildStatus}
                      runLogs={streaming.logs}
                      runStatus={streaming.status}
                      runProgress={streaming.progress}
                      streamingLogs={streaming.streamingLogs}
                      projectId={projectId}
                      userContext={{
                        id: user?.id || null,
                        email: user?.email || null,
                        displayName: profile?.display_name || null,
                        tier: subscriptionTier,
                        locale,
                        creditsAvailable: usageData?.credits?.totalRemaining ?? 0,
                        creditsLoaded: !usageLoading && usageData !== null,
                        creditCosts: modelCreditCosts,
                        maxPayloadBytes: MINIAPP_MAX_PAYLOAD_BYTES[subscriptionTier] ?? MINIAPP_MAX_PAYLOAD_BYTES.free,
                        embeddingBatchLimit: EMBEDDING_BATCH_LIMITS[subscriptionTier] ?? EMBEDDING_BATCH_LIMITS.free,
                      }}
                      familyConfig={project?.family_params ? {
                        params: project.family_params,
                        description: project.description,
                        title: project.name,
                      } : null}
                      onFrontendError={handleFrontendError}
                      projectUpdatedAt={project?.updated_at}
                    />
                    {previewError && (
                      <div className="mt-2 flex items-center gap-2 text-theme-error text-xs sm:text-sm">
                        <AlertCircle className="h-4 w-4" />
                        {previewError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}

          {/* Streaming response */}
          {sending && (
            <div className="flex gap-2 sm:gap-4">
              <div className="flex h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 items-center justify-center rounded-xl bg-theme-surface">
                <Code className="h-4 w-4 sm:h-5 sm:w-5 text-theme-text-secondary" />
              </div>
              <div className="flex-1 max-w-[85%] sm:max-w-3xl">
                {/* Fun video shown by default during streaming */}
                {streamingVideo && !showStreamingText ? (
                  <>
                    <div className="bg-theme-surface rounded-2xl rounded-tl-md px-3 sm:px-4 py-2 sm:py-3">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-theme-text-muted">
                          <RefreshCw className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
                          <span>{streamingStatus || 'Working on it...'}</span>
                        </div>
                        <p className="text-xs sm:text-sm text-theme-text-secondary italic">
                          Enjoy this <span className="font-medium text-accent-500">{streamingVideo.name}</span> while I work on your app.
                        </p>
                        <video
                          src={getVideoSrc(streamingVideo)!}
                          controls
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="rounded-lg max-w-full"
                          style={{ maxHeight: '300px' }}
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => setShowStreamingText(true)}
                      className="flex items-center gap-1 mt-1 text-[10px] sm:text-xs text-theme-text-muted hover:text-accent-500 transition-colors"
                      title="Show actual agent output"
                    >
                      <Eye className="h-3 w-3" />
                      <span>Show actual response</span>
                    </button>
                  </>
                ) : (
                  <>
                    {streamingStatus && (
                      <div className="flex items-center gap-2 mb-2 text-xs sm:text-sm text-theme-text-muted">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        <span>{streamingStatus}</span>
                      </div>
                    )}
                    {streamingContent ? (
                      <div className="bg-theme-surface rounded-2xl rounded-tl-md px-3 sm:px-4 py-2 sm:py-3">
                        <pre className="whitespace-pre-wrap font-sans text-xs sm:text-sm text-theme-text">{streamingContent}</pre>
                      </div>
                    ) : !streamingStatus && (
                      <div className="bg-theme-surface rounded-2xl rounded-tl-md px-3 sm:px-4 py-2 sm:py-3">
                        <div className="flex gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-theme-text-muted animate-pulse-soft" />
                          <span className="h-2 w-2 rounded-full bg-theme-text-muted animate-pulse-soft animate-delay-200" />
                          <span className="h-2 w-2 rounded-full bg-theme-text-muted animate-pulse-soft animate-delay-400" />
                        </div>
                      </div>
                    )}
                    {streamingVideo && (
                      <button
                        onClick={() => setShowStreamingText(false)}
                        className="flex items-center gap-1 mt-1 text-[10px] sm:text-xs text-theme-text-muted hover:text-accent-500 transition-colors"
                        title="Show fun video instead"
                      >
                        <Film className="h-3 w-3" />
                        <span>Show fun video</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-3 p-3 sm:p-4 rounded-lg bg-theme-error-bg border border-theme-error-border text-theme-error max-w-2xl mx-auto">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-xs sm:text-sm">{error}</p>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-theme-border-muted p-3 sm:p-4">
          <input
            ref={chatAttachmentInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleChatAttachmentChange}
            disabled={sending || chatAttachments.length >= MAX_CHAT_ATTACHMENTS}
          />
          <form onSubmit={handleSubmit} className="flex gap-2 sm:gap-3 items-start max-w-4xl mx-auto">
            {/* Model selector dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
                disabled={sending}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 h-10 sm:h-12 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Select model"
              >
                <span className="text-xs sm:text-sm font-medium text-theme-text whitespace-nowrap">
                  {/* Show abbreviated name on mobile */}
                  <span className="sm:hidden">
                    {selectedModelInfo.name.includes('Opus') ? 'Opus' : selectedModelInfo.name.includes('Sonnet') ? 'Sonnet' : selectedModelInfo.name.includes('GLM') ? 'GLM' : selectedModelInfo.name.includes('MiniMax') ? 'MiniMax' : 'Kimi'}
                  </span>
                  <span className="hidden sm:inline">
                    {selectedModelInfo.name.replace('Claude ', '')}
                  </span>
                </span>
                <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 text-theme-text-muted transition-transform ${modelDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {modelDropdownOpen && (
                <div className="absolute bottom-full left-0 mb-2 w-48 sm:w-56 rounded-lg bg-theme-surface border border-theme-border-muted shadow-lg overflow-hidden z-10">
                  {MODELS.map((model) => (
                    <button
                      key={model.id}
                      type="button"
                      onClick={() => handleModelChange(model.id)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-theme-surface-hover transition-colors ${
                        selectedModel === model.id ? 'bg-theme-surface-hover' : ''
                      }`}
                    >
                      <div>
                        <div className="text-xs sm:text-sm font-medium text-theme-text">{model.name}</div>
                        <div className="text-[10px] sm:text-xs text-theme-text-muted">{model.description}</div>
                      </div>
                      {selectedModel === model.id && (
                        <Check className="h-4 w-4 text-accent-500" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleOpenChatAttachmentPicker}
              disabled={sending || chatAttachments.length >= MAX_CHAT_ATTACHMENTS}
              className="flex items-center justify-center h-10 sm:h-12 w-10 sm:w-12 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors disabled:opacity-50"
              title="Attach files"
            >
              <Paperclip className="h-4 w-4 text-theme-text-muted" />
            </button>

            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe the changes..."
                rows={1}
                className="input py-2 sm:py-3 pr-10 sm:pr-12 resize-none text-sm sm:text-base"
                style={{
                  minHeight: '40px',
                  lineHeight: '24px',
                }}
                disabled={sending}
              />
              <button
                type="submit"
                disabled={!input.trim() || sending}
                className="absolute right-2 top-1 sm:top-2 p-1.5 sm:p-2 rounded-lg bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sending ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </div>
          </form>
          {chatAttachments.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 max-w-4xl mx-auto">
              {chatAttachments.map((file, index) => (
                <span
                  key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                  className="inline-flex items-center gap-1 rounded-full bg-theme-surface border border-theme-border-muted px-2 py-0.5 text-[10px] text-theme-text"
                >
                  <Paperclip className="h-2.5 w-2.5 text-theme-text-muted" />
                  <span className="max-w-[180px] truncate">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveChatAttachment(index)}
                    className="rounded p-0.5 hover:bg-theme-surface-hover"
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="h-2.5 w-2.5 text-theme-text-muted" />
                  </button>
                </span>
              ))}
              <button
                type="button"
                onClick={clearChatAttachments}
                className="inline-flex items-center rounded-full border border-theme-border-muted px-2 py-0.5 text-[10px] text-theme-text-muted hover:text-theme-text hover:bg-theme-surface"
              >
                Clear
              </button>
              <span className="text-[10px] text-theme-text-muted ml-auto">
                {chatAttachments.length}/{MAX_CHAT_ATTACHMENTS} attached
              </span>
            </div>
          )}
          <p className="text-[10px] sm:text-xs text-theme-text-muted text-center mt-2">
            <span className="hidden sm:inline">Press Enter to send, Shift+Enter for new line</span>
            <span className="sm:hidden">Enter to send</span>
          </p>
        </div>
      </div>

      {/* Error Submission Modal */}
      <Modal
        isOpen={showErrorModal}
        onClose={handleDismissError}
        title={pendingErrorType === 'build' ? 'Build Error' : 'Execution Error'}
        size="sm"
      >
        <div className="text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-theme-error-bg mb-4">
            <AlertCircle className="h-7 w-7 text-theme-error" />
          </div>
          <p className="text-theme-text-secondary mb-2">
            {pendingErrorType === 'build'
              ? 'Your mini-app backend failed to build.'
              : 'Your mini-app encountered an error while running.'}
          </p>
          <p className="text-sm text-theme-text-muted mb-6">
            Would you like to send this error to the coding AI so it can help fix the issue?
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={handleDismissError}
              className="btn btn-secondary flex-1"
            >
              Dismiss
            </button>
            <button
              onClick={handleSubmitError}
              className="btn btn-primary flex-1"
            >
              Send to AI
            </button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
